// Production i18n namespace (Module 04 §4, slice 04-A: S1–S4). Wired into
// apps/web's aggregated `dictionaries` (src/i18n-dictionaries.ts) so the CI
// key-parity gate (`pnpm run i18n:parity`, TESTING §12.1 / D-014) covers it
// automatically, and declared in src/i18n-completeness.ts so the VALUE gate
// knows which locales are real.
//
// `productionEn` is authoritative; `ProductionDict = typeof productionEn` makes
// every locale parity-checked at COMPILE time (a missing or extra key fails tsc).
// `ru` is a real translation (production.ru.ts) — KazKabel's planners and
// storekeepers drive work orders in Russian. fr, zh, zh-Hant and hi live in
// their own production.<locale>.ts files, loaded only with that locale (D-430).
//
// ENTITY NOUNS ARE NOT IN HERE. "Work order", "Work center", "Product",
// "Facility", "Lot", "Scrap", "BOM" are TERMINOLOGY keys resolved through
// `useTerm()` (module 02 §2.5) — a standalone label for one of those nouns must
// be `term("work_order")`, never a dictionary key. Prose that mentions the
// concept inside a sentence lives here (the waste namespace's precedent:
// "No waste types yet"), because a sentence cannot be assembled from a
// tenant-overridable singular label without breaking its grammar in ru.

import type { Translated } from "../../../i18n-shape.js";
import { productionRuDict } from "./production.ru.js";

export const productionEn = {
  nav: {
    aria: "Production sections",
    // The work-order list link's label is `term("work_order")`; "Dispatch" is
    // not a terminology key, so it is ours to translate.
    dispatch: "Dispatch board",
    // 04-B: S7 + S8.
    conflicts: "Conflicts",
    devices: "Devices",
    // 04-C-2: S6.
    calibration: "Calibration",
    // 04-C-4: S5.
    shifts: "Shifts",
    // S13 — the blocked-stations board. Rides `production.telemetry.read`, the
    // same code as Calibration and as e22 itself.
    blocked: "Blocked",
    // S14 — the piece-rate ledger, on `production.piece_rate.read` (e27's own
    // fence). Not "Piece rate": the entry is a QUANTITY, and calling the nav
    // entry after the money would promise a payslip this release cannot serve.
    pieceRate: "Piece quantity",
  },

  // The pegged sales order line, as module 09 labelled it for the reader
  // (D-391 ruling 3): its number, line and customer, or "Sales order" for a
  // reader its customer door refuses.
  salesOrder: {
    hidden: "Sales order",
    line: "line {{line}}",
  },

  common: {
    retry: "Retry",
    requestId: "Request ID",
    loading: "Loading…",
    save: "Save",
    saving: "Saving…",
    cancel: "Cancel",
    /** The ✕ on a modal — an accessible name, never a visible word. */
    close: "Close",
    confirm: "Confirm",
    back: "Back",
    actions: "Actions",
    /** An empty cell (a null due date, a not-yet timestamp). NEVER used for a
     * redacted money field — a redacted column is ABSENT, not dashed (§7). */
    notAvailable: "—",
    priorityLabel: "Priority {{value}}",
    overdue: "Overdue",
  },

  // The §2 closed vocabularies. These ARE ours to translate (contract enums),
  // unlike the entity nouns beside them.
  status: {
    wo: {
      draft: "Draft",
      released: "Released",
      in_progress: "In progress",
      completed: "Completed",
      closed: "Closed",
      cancelled: "Cancelled",
    },
    op: {
      queued: "Queued",
      ready: "Ready",
      in_progress: "In progress",
      paused: "Paused",
      completed: "Completed",
      skipped: "Skipped",
    },
  },

  costType: {
    material: "Material",
    labor: "Labor",
    overhead: "Overhead",
    waste_variance: "Waste variance",
  },

  issueMethod: {
    scan: "Scanned",
    backflush: "Backflushed",
    manual: "Manual",
    remnant_match: "Matched from an off-cut",
  },

  // §2.4 capture_source — "the tablet said 5980" and "someone typed 5980" are
  // never the same fact (§3.11), so the two are named differently on sight.
  captureSource: {
    office: "Keyed in the office",
    floor: "Reported from the floor",
  },

  // §2.18 output kinds: `co_product` / `by_product` are terminology keys and
  // resolve through useTerm(); `finished` is not, so its label lives here.
  outputKind: {
    finished: "Finished product",
  },

  // ---- S1 — work order list (/production/work-orders) ----------------------
  list: {
    createButton: "Create a work order",
    col: {
      number: "Number",
      progress: "Progress",
      status: "Status",
      due: "Due",
      costToDate: "Cost to date",
      salesOrder: "Sales order",
    },
    progressOf: "{{good}} of {{planned}}",
    filter: {
      status: "Status",
      any: "Any",
      // Own key for the facility select: Russian agrees the pronoun with the
      // noun's gender («площадка» is feminine — «Любая», not «Любой»).
      anyFacility: "Any",
      from: "From",
      to: "To",
      overdueOnly: "Overdue only",
      search: "Search",
      searchPlaceholder: "Number or product…",
      sort: "Sort",
      sortDue: "By due date",
      sortPriority: "By priority",
      sortCreated: "By creation date",
      clear: "Clear filters",
      filters: "Filters",
      filtersCount: "Filters ({{n}})",
      done: "Done",
      remove: "Remove {{label}}",
    },
    // The names of the three system saved views (spec 04 §4 S1, spec 16
    // §3.12 item 10). Migration 0070 stores each key as its view's `name`, and
    // the views switcher renders a system name through `t()` (D-305 ruling
    // 11), so these keys are data now: renaming one strands the stored name.
    chips: {
      overdue: "Overdue",
      inProgress: "In progress",
      awaitingClose: "Awaiting close",
    },
    emptyTitle: "No work orders yet",
    // §4 S1's original copy pointed at Planning (module 03), which is not
    // built (D-071). Since D-290 a work order is also planned from a customer
    // order's production line, so the copy names that door, and no longer says
    // anything "arrives with a later module".
    emptyBody: "Create one manually, or plan one from a production line on a customer order.",
    // For read-only principals the create invitation is a door that is not
    // there; say what the screen IS instead.
    emptyBodyReadOnly: "Work orders appear here once a planner creates and releases them.",
    // A reader whose work-order read is the sales own-book breadth (D-391
    // ruling 1) reads the work orders of their own book and cannot create
    // one, so their empty list says which work orders it holds for them.
    emptyTitleOwnBook: "No work order for your orders yet",
    emptyBodyOwnBook:
      "This list holds the work orders planned for orders you own or for customers assigned to you. Each order's page lists its work orders too.",
    // "Nothing matches" and "there are none" are different facts; only the
    // second deserves the create CTA (the inventory precedent).
    emptyFilteredTitle: "Nothing matches these filters",
    emptyFilteredBody: "Loosen or clear the filters to see more.",
    error: "Could not load the work orders",
    // The list read paused for want of a network (R2, PR-E4 wave 4): said
    // as offline, never shown as a skeleton that does not end.
    offlineTitle: "No network, so the list is paused",
    offlineBody: "It fills in by itself the moment the connection is back.",
    loadMore: "Load more",
  },

  // ---- S2 — work order detail (/production/work-orders/:id) ----------------
  detail: {
    error: "Could not load the work order",
    backToList: "Back to the list",
    tabsAria: "Work order sections",
    tab: {
      operations: "Operations",
      materials: "Materials",
      costs: "Costs",
      // 04-C-2: the §4 tab rule — added only now that its table exists.
      calibration: "Calibration",
      // 04-C-3: same rule, same reason — `operation_serial_lot_captures` now
      // exists (migration 0027), so the tab is admitted.
      genealogy: "Genealogy",
      // 2026-08-08: §5 e10's timeline — the tab §4's head-note recorded as
      // missing (its table has existed since 04-B, so it was overdue).
      events: "Events",
    },
    meta: {
      due: "Due",
      priority: "Priority",
      quantity: "Quantity",
      notes: "Notes",
      created: "Created",
      cancelReason: "Cancel reason",
      // P3-H-3 (D-391 ruling 3): whom the draft is for.
      salesOrder: "Sales order",
    },
    action: {
      edit: "Edit draft",
      release: "Release",
      complete: "Complete",
      close: "Close",
      cancel: "Cancel",
      // From `released` the ONE editable field is priority (§5 row 8), so the
      // control says so rather than offering to edit a draft that no longer
      // exists (F14).
      editPriority: "Change priority",
      traveler: "Traveler PDF",
      // §4 "Mobile behavior": transitions are disabled below `md`.
      mobileHint: "State changes are disabled on a phone — use a desktop.",
    },
    // D-299 owner ruling R10: a refused close, worded from the refusal's
    // structured blockers — one line per blocker, then EITHER the link to the
    // screen that clears it (for a reader who can) OR who can. Never a spec
    // section number or a route: finance presses Close too (§5 row 7).
    closeBlocked: {
      downtime: "{{count}} stoppage(s) recorded against this order's steps are still open.",
      downtimeSince: "Stopped since {{times}}.",
      downtimeLink: "End it on the blocked-stations board",
      // The who-sentences follow the API's own WO_CLOSE_BLOCKED details
      // (D-299 round 4): the downtime arm names the station's tablet as the
      // second ender, because the floor's own End still works while the
      // station shows live work.
      downtimeWho:
        "A facility or group admin can end them on the blocked-stations board, or the station's tablet while it shows live work.",
      conflicts: "{{count}} floor conflict(s) about this order are still open.",
      conflictsLink: "Resolve them in the sync-conflict queue",
      conflictsWho: "A facility or group administrator resolves them in the sync-conflict queue.",
      handoffs: "{{count}} defect hand-off(s) on this order are still undecided.",
      handoffsLink: "Decide them on the shift scoreboard",
      handoffsWho:
        "A facility or group administrator, or quality, decides them on the shift scoreboard.",
    },
    editForm: {
      title: "Edit draft",
      titlePriority: "Change priority",
      qtyPlanned: "Planned quantity",
      dueDate: "Due date",
      priority: "Priority",
      notes: "Notes",
      priorityHint: "Priority stays editable until the work starts; everything else is draft-only.",
    },
    cancelForm: {
      title: "Cancel this order",
      reason: "Reason",
      dispositionTitle: "What happens to the good quantity already produced?",
      receiveQty: "Receive to stock",
      scrapQty: "Scrap",
      scrapReason: "Scrap reason",
      dispositionHint:
        "Everything produced but not yet received must be received or scrapped in the same step, so no value is orphaned.",
      // Was "returns arrive with the floor slice (04-B) — this cancel cannot be
      // cleared yet". That leaked an internal slice id AND was false: returns
      // shipped with 04-B months ago, so the screen was telling a planner a
      // capability did not exist while the server was refusing them for a
      // reason they could actually clear. It now names both real exits, in the
      // same terms as the server's own refusal.
      issuedBlockedNote:
        "Material is still issued to this order. Return it to stock from the floor or as a negative issue, or scrap it here.",
      confirm: "Cancel the order",
    },
    ops: {
      colSeq: "Step",
      colName: "Name",
      colStatus: "Status",
      colQty: "Good / scrap",
      colPlanned: "Planned",
      colActual: "Actual",
      colSetupMin: "Setup min",
      colRunMin: "Run min",
      colMachineMin: "Machine min",
      skippedReason: "Skipped: {{reason}}",
      skip: "Skip",
      skipTitle: "Skip this step",
      // 04-C-5 — §3.2's rework cycle and §2.2's quality hold, WORDED on the
      // row: both render as `in_progress`, and neither is what that word means
      // on its own.
      reworkBadge: "Rework",
      qcHoldBadge: "On quality hold",
      releaseHold: "Release hold",
      releaseHoldTitle: "Release the quality hold",
      releaseHoldHint:
        "Clearing a hold overrides a quality decision. The reason is written to the audit trail and cannot be edited afterwards.",
      releaseHoldNote: "Reason",
      skipReason: "Reason",
      skipHint: "The reason is the permanent record of why a planned step never ran.",
      record: "Record result",
      recordTitle: "Record a result",
      qtyGood: "Good quantity",
      qtyScrap: "Scrap quantity",
      qtyInvalid: "Quantities are decimal numbers with up to three decimal places.",
      scrapReason: "Scrap reason",
      pickReason: "Pick a reason…",
      scrapReasonRequired: "A scrap reason is required when any scrap is reported.",
      startedAt: "Started at",
      endedAt: "Ended at",
      timesHint:
        "Record both times to absorb machine overhead; leave both empty when nobody recorded a clock.",
      complete: "Step finished — mark it complete",
      note: "Note",
      submit: "Record",
      // e34's DOWNTIME_END_AT_INVALID (before_window, D-299 owner rulings S1
      // and S4), worded for this form: its reader stated the STEP's end.
      stoppageEndBeforeWindow:
        "Nothing was saved: the step's open stoppage would end over 24 hours ago. Enter a later end time, or end it on the blocked-stations board.",
      recorded: "Recorded.",
      reportsTitle: "Recorded results",
      reportsEmpty: "No results recorded yet.",
      colSource: "Recorded via",
      colAt: "At",
      // §3.3 step 7 keyed off the paper completion sheet (D-084). Shown only
      // when the step requires measured capture, because that is the only
      // step where the sheet carries these numbers.
      calTitle: "Measured barrel (this step requires measured capture)",
      calHint:
        "Costing and stock use the scale's measured value; the machine's count is only compared against it.",
      calBatch: "Barrel / batch label",
      calClaimed: "Machine claimed",
      calMeasured: "Measured",
      calUom: "Unit",
      calMethod: "How it was measured",
      calScan: "Output barrel scan (optional)",
      calScanHint:
        "The QR or serial of the stock unit this batch landed on. Leave empty if the barrel is not labelled yet.",
      calRequired:
        "This step requires measured capture: fill in the barrel label, the machine-claimed quantity and the measured quantity, or record the result without completing it.",
      calQtyInvalid: "Claimed and measured are positive decimals with up to three decimal places.",
      // §3.3 step 8 keyed off the paper traveler (D-085 pt 3, 04-C-3). Shown
      // only when the step requires serial capture — the D-084 rule above,
      // applied to lineage. The DIRECTION WORDS ARE THE FLOOR'S OWN
      // (`production.floor.op.serialDirections`): the same physical event must
      // not be describable in two vocabularies depending on which screen keyed
      // it. "Labelled unit" is the Genealogy tab's noun on this same screen.
      serialTitle: "Serials from the traveler (this step requires serial capture)",
      serialHint:
        "Key one row per unit the traveler names: its scan code, engraved serial, or both.",
      serialAlreadyCaptured:
        "The floor already captured a serial for this step. Add a row only for a unit the tablet missed.",
      serialRow: "Labelled unit {{index}}",
      serialRemove: "Remove",
      serialRemoveRow: "Remove labelled unit {{index}}",
      serialDirection: "Which way?",
      serialDirections: {
        produced: "Came off this machine",
        consumed: "Went into this machine",
      },
      serialDirectionNote: {
        produced: "Recording what this step PRODUCED — this is what a completion needs.",
        consumed: "Recording what this step CONSUMED — the input side of the lineage.",
      },
      serialScan: "Scan code from the label",
      serialNo: "Engraved serial",
      serialQty: "Measured length / weight",
      serialUom: "Unit",
      serialAdd: "Add another labelled unit",
      serialAddHint:
        "Add a row when one traveler entry labelled several units, such as drums cut from one run.",
      serialRequired:
        "This step needs a serial: key at least one unit's scan code or engraved serial, or record without completing.",
      serialProblem: {
        nothing_identified:
          "Labelled unit {{index}}: the scan code or the engraved serial is required — one of the two identifies the unit.",
        qty_required: "Labelled unit {{index}}: the measured length or weight is missing.",
        qty_invalid:
          "Labelled unit {{index}}: the measured length or weight is a positive decimal with up to three decimal places.",
        uom_required: "Labelled unit {{index}}: the unit of measure is missing.",
      },
    },
    // ---- Genealogy tab (§4 S2, slice 04-C-3) ------------------------------
    genealogy: {
      consumedLabel: "Consumed",
      producedLabel: "Produced",
      consumedTitle: "Went into this order",
      producedTitle: "Came out of this order",
      consumedBody:
        "Lots and units a worker scanned as going INTO the run — the input side of the lineage a recall is traced through.",
      producedBody:
        "Labelled units and engraved serials the run produced. Measured quantities here override the bill of materials' ratio when the order completes.",
      consumedNone: "No input was scanned on this order.",
      producedNone: "Nothing has been labelled on this order yet.",
      // The two empty states that mean different things (§4: pending is
      // stated, never drawn as an absence).
      emptyPending:
        "No serial or lot is captured yet. The operator records one before the serial-capture step completes.",
      emptyNotRequired:
        "No step on this order captures serials or lots, so there is no lineage to show.",
      colIdentity: "Identity",
      colProduct: "Product",
      colQty: "Quantity",
      colStep: "Step",
      colWhen: "Captured",
      colSource: "Recorded by",
      noProduct: "No registered unit — engraved serial only",
      openUnit: "Open unit",
      openTrace: "Trace lineage",
      source: {
        floor: "Floor device",
        office: "Office (traveler)",
      },
    },

    // ---- Events tab (§4 S2 — §5 e10 / §2.3; built 2026-08-08) -------------
    events: {
      error: "Could not load the event timeline",
      emptyTitle: "No events yet",
      emptyBody:
        "Nothing recorded from a device yet. Results keyed in the office are on the Operations tab.",
      // "Nothing matches the filter" and "there are none" are different facts.
      emptyFilteredTitle: "No events of this kind",
      emptyFilteredBody:
        "This order has events, just none of the picked kind — clear the filter to see them all.",
      filterKind: "Kind",
      filterAll: "All kinds",
      // D-089 (facility-clock rule): the reader is told WHOSE 14:30 this is,
      // on screen. (This comment briefly read D-091, which is the S10 numpad
      // decision — corrected to the number the §4 S2 note actually cites.)
      timezoneNote: "Times are the facility's own clock ({{timezone}}).",
      timezoneNoteFallback:
        "The facility's timezone is not available — times are shown in your browser's clock.",
      loadMore: "Load more",
      colWhen: "When",
      colKind: "Event",
      colDetail: "Details",
      colSource: "Recorded via",
      recordedAt: "Recorded {{when}}.",
      // §9.5, both honestly derived from the two wire instants: >24 h skew was
      // applied AND flagged for review; a fast clock was clamped for shift
      // attribution while the row keeps the device's instant verbatim.
      staleFlag:
        "The device clock was {{hours}} h off. Recorded and flagged for review; it counts to the shift at the time it arrived.",
      clampedFlag:
        "The device clock was ahead, so this counts to the shift at the time it arrived.",
      // The eleven §5.1 kinds in words, plus the two the wire distinguishes by
      // content: a NEGATIVE material_issued is the floor return (D-081), and a
      // §2.17 correction payload is an office correction, never one more
      // report.
      kind: {
        op_started: "Step started",
        op_paused: "Step paused",
        op_resumed: "Step resumed",
        op_completed: "Step completed",
        qty_reported: "Quantity reported",
        material_issued: "Material issued",
        material_returned: "Material returned",
        downtime_started: "Downtime started",
        downtime_ended: "Downtime ended",
        calibration_recorded: "Measured barrel recorded",
        serial_lot_captured: "Serial / lot captured",
        defect_handoff_flagged: "Defect flagged at hand-off",
        correction: "Quantity correction",
      },
      source: {
        // §3.11's distinction, in the §2.4 words this screen already uses
        // (production.captureSource.*) — plus the third origin only this
        // timeline has: a correction composed by conflict review.
        correction: "Office correction (conflict review)",
      },
      detail: {
        qty: "{{good}} good / {{scrap}} scrap",
        issued: "{{qty}} {{uom}} issued",
        returned: "{{qty}} {{uom}} returned to stock",
        calibration: "Machine claimed {{claimed}}, measured {{measured}} {{uom}}",
        defect: "{{qty}} defective · {{reason}}",
        downtimeReason: "Reason: {{reason}}",
        correctionRef: "Corrects the event reviewed in conflict",
      },
    },

    materials: {
      colPlanned: "Planned",
      colReserved: "Reserved",
      notReserved: "Not reserved: {{qty}} {{uom}}",
      notReservedHint:
        "Part of the plan is neither issued nor reserved: a first issue settled its reservation, or the scrap allowance was added after release.",
      colIssued: "Issued",
      colVariance: "Variance",
      colCost: "Issued cost",
      colMethod: "How",
      issue: "Issue material",
      issueTitle: "Issue material",
      qty: "Quantity",
      uom: "Unit",
      forStep: "Against step",
      noStep: "Whole order (no step)",
      submit: "Issue",
      issued: "Issued.",
      qtyPositive: "The quantity must be greater than zero.",
      empty: "No components planned — the pinned BOM has no lines.",
      // 04-B-4 (D-076/D-081): the return action — this REPLACED the
      // "returns arrive with 04-B" pending note.
      historyTitle: "Issues & returns",
      historyEmpty: "Nothing issued yet.",
      colWhen: "When",
      colRemaining: "Returnable",
      methodLabel: {
        manual: "Manual",
        scan: "Floor scan",
        backflush: "Backflush",
        remnant_match: "Remnant match",
      },
      returnRow: "Return of an earlier issue",
      returnAction: "Return",
      returnTitle: "Return to stock",
      returnQty: "Quantity to return",
      returnHint:
        "The return posts its own stock movement at the original issue's captured cost — the issue itself stays on the record.",
      returnExceeds: "That is more than remains issued on this line.",
      returnSubmit: "Return to stock",
      returned: "Returned.",
      // D-308: a completed order takes leftover material back until it is
      // closed, and takes no further issue. Shown above the tab on a
      // completed order to a reader who can return.
      returnAfterCompletionHint:
        "This work order is completed. It takes leftover material back until it is closed, and it takes no further issue.",
      // e30's 409 WO_CLOSED_IMMUTABLE with `way_on: inventory_stock_adjustment`
      // (D-308): the order closed while the return form was open. Worded
      // from `way_on`; the server's English `detail` is never shown.
      returnClosedWayOn:
        "Nothing was returned: the order closed first, and a closed order takes no material back. Record the material with a stock adjustment in Inventory.",
      // The D-308 review: a closed order offers no Return, and a refetch that
      // finds it closed takes an open form away, so a reader who can return
      // is told where leftover material goes. Shown while any issue still has
      // an unreturned remainder. For a holder of `inventory.adjust` it links
      // to spec 06 §4.12 (D-315); anyone else reads it unlinked.
      returnAfterCloseHint:
        "This order is closed and takes no material back. Record leftover material with a stock adjustment in Inventory.",
      // Module 06's 422 UNIT_NOT_AVAILABLE on a return (the D-308 review): the
      // container the material came from is held by quality or in transit
      // (D-308 round 2 keeps its name on purpose), or a concurrent draw
      // emptied it, in which case a second press returns it as bulk.
      returnUnitNotAvailable:
        "Nothing was returned: the source container can't take it back now (held, in transit or just emptied). Try again once it is released.",
    },
    costs: {
      totalsTitle: "Cost to date",
      // §3.11: an office-recorded result writes no labor entry, so a zero here
      // is a structural absence. LABELLED, never shown as a computed zero.
      // Reworded 2026-08-08 (the M04-S12 labor uplift): the floor timer has
      // shipped, so "arrives with 04-B" stopped being true — the honest zero
      // statement is now about THIS order, not about a pending slice.
      pendingLabor:
        "No labor captured: office results carry no timer. Labor comes from floor timers or a manual labor adjustment.",
      ledgerTitle: "Entries",
      colType: "Type",
      colSource: "Source",
      colAmount: "Amount",
      colAt: "Entered",
      ledgerEmpty: "No cost entries yet.",
      loadMore: "Load more",
      source: {
        material_issue: "Material issue",
        labor_time_entry: "Labor time",
        machine_time: "Machine time",
        calibration_variance: "Calibration variance",
        scrap_beyond_standard: "Scrap beyond standard",
        manual_adjustment: "Manual adjustment",
      },
      outputsTitle: "Outputs",
      colKind: "Kind",
      colRatio: "Ratio",
      colShare: "Share",
      colProduced: "Produced",
      colNrv: "NRV",
      // §2.18: no Module 09 price list — a missing NRV source is SAID, never a
      // silent 0.
      noNrv: "No price source for this by-product yet. It is unpriced, not zero.",
      adjust: "Manual adjustment",
      adjustTitle: "Post a manual adjustment",
      adjustType: "Cost type",
      // The currency is NAMED and the typed value is echoed back formatted:
      // "minor units" alone left the user to guess both the currency and the
      // scale of a number that writes a permanent ledger entry.
      adjustAmountIn: "Amount ({{currency}}, minor units, signed)",
      adjustScale: "Whole minor units of {{currency}} — negative for a credit.",
      adjustPreview: "Posts as {{amount}}.",
      adjustAmountInvalid:
        "The amount must be a whole number of minor units (negative for a credit).",
      adjustReason: "Reason",
      adjustHint: "The ledger is append-only: a correction is a compensating entry, never an edit.",
      adjustSubmit: "Post",
      adjusted: "Posted.",
    },
  },

  // ---- S3 — create/release modal --------------------------------------------
  createModal: {
    stepCreate: "Create",
    stepRelease: "Release",
    searchLabel: "Find a product",
    searchPlaceholder: "Search by SKU or name…",
    noProducts: "Nothing matches.",
    // A create form's facility is REQUIRED — "Any" is a filter's word.
    pickFacility: "Choose a facility",
    // The server's own refusal, stated BEFORE the click that would collect it.
    noEffectiveBom:
      "No BOM version is effective for this product at this facility today — activate one in the catalogue, or pin a version above.",
    noEffectiveRouting:
      "No routing version is effective for this product at this facility today — activate one in the catalogue, or pin a version above.",
    versionsTitle: "Pinned versions",
    // "Routing" is NOT one of the 61 terminology keys (only `routing_step` is),
    // so its label is ours to translate.
    routing: "Routing",
    activeVersion: "Active",
    overrideVersion: "Choose another version",
    versionOption: "Version {{version}}",
    versionWarning:
      "This is not the active version — the order will be built from an older revision. Make sure that is deliberate.",
    pickFacilityFirst: "Pick a facility first — versions are pinned per facility.",
    qty: "Quantity",
    qtyInvalid: "Enter a quantity greater than zero, with up to three decimal places.",
    uom: "Unit",
    dueDate: "Due date",
    priority: "Priority",
    notes: "Notes",
    departmentOptional: "Optional",
    submitCreate: "Create draft",
    created: "Draft created.",
    atpTitle: "Availability per component",
    atpOk: "Available",
    atpShort: "Short by {{qty}}",
    atpUnknown: "Availability unknown",
    atpAdvisory: "A preview — the shortage list recorded at release is the authoritative one.",
    // §4 S3's "earliest PO due from Module 10" cannot be shown: module 10 is
    // not built. Shortfall only, degraded note (spec §4 S3 amended by the
    // screens change).
    releaseSubmit: "Release",
    releaseLater: "Release later",
    releaseAnywayTitle: "Release with shortages?",
    releaseAnywayBody:
      "{{count}} component(s) are short. Releasing anyway records the shortage on the order.",
    releaseAnyway: "Release anyway",
    released: "Released.",
    shortagesTitle: "Shortages recorded at release",
    colRequired: "Required",
    colAvailable: "Available",
    colShortfall: "Shortfall",
    done: "Done",
    // ---- the PRE-PEGGED create (D-290): opened from a sales order line's
    // "Plan a work order", the work order is made FOR that line. The banner
    // names the order and the line, the product is the line's and cannot be
    // changed (the server refuses any other with ORDER_LINE_PRODUCT_MISMATCH),
    // and the quantity starts at what the line still has to ship.
    pegBanner: "For customer order {{order}} · line {{line}}",
    pegLoading: "Loading the customer order…",
    pegError: "The customer order this work order is for could not be loaded.",
    pegBackToOrder: "Open the order",
    pegProductLocked: "Set by the order line.",
    // A 403 in this modal is a refusal the planner must act on, so it is
    // said in the screen's language, never as the server's English title.
    // The pegged release names its own cause (`reason:
    // order_facility_out_of_scope`): the releaser's scope does not cover the
    // facility the customer order ships from, and the draft stays a draft.
    releaseOrderFacilityOutOfScope:
      "The customer order ships from a facility outside your access. A planner for that facility must release this draft.",
    permissionDenied: "You do not have permission to do that.",
  },

  // ---- S4 — dispatch board (/production/dispatch) ---------------------------
  dispatch: {
    title: "Dispatch board",
    boardAria: "Dispatch board",
    facilitiesError: "The facility list could not be loaded.",
    pickFacilityFirst: "Pick a facility to see its stations.",
    pickDepartmentFirst: "Pick a department to lay out its stations.",
    noWorkCenters: "No work centres in this department.",
    emptyColumn: "Nothing queued at this station.",
    columnError: "This station's queue could not be loaded.",
    stationAbsent: "This station no longer exists.",
    qtyOf: "{{reported}} of {{planned}}",
    dueOn: "Due {{date}}",
    // D-072: the card may legally not land where it was dropped — the server's
    // computed order is the law, and the toast says so.
    priorityToast: "Priority set to {{value}}. The queue order follows it.",
    crossColumnRefused:
      "Moving a job between stations is scheduling, which the Planning schedule board decides.",
    // P3-I-2 (D-399 ruling 7): a drag at a facility with a published schedule
    // makes the planner solve at once, and the card says so until it publishes.
    scheduleUpdating: "Schedule updating",
    readOnlyPhone: "Read-only on a phone — drag to re-prioritise from a desktop.",
    dragHint: "Drag within a column to set priority (1–5).",
    // The card's blocked markers. The data has ridden e14 since 04-C-5 and no
    // office screen read it, so a planner re-prioritising a column could not
    // see that the card at the top of it cannot be worked at all.
    cardHoldBadge: "Quality hold",
    cardReworkBadge: "Re-do",
    cardBlockedAria: "This step is blocked",
  },

  // ---- S13 — blocked stations (/production/blocked) -------------------------
  //
  // Every string here is read at 07:00 by somebody deciding where to walk, so
  // none of them describes: they name the thing and the next move.
  blocked: {
    title: "Blocked stations",
    tableCaption: "Stations that are not running, and why",
    colBlockedBy: "Blocked by",
    colWhy: "Reason",
    colFor: "For",
    colWaiting: "Waiting behind",
    // D-123 ruling 9: three unrelated things in this platform are called a
    // hold. The operation flag is spoken as the whole phrase wherever it can be
    // read beside a quarantine record — and this screen links to the quarantine
    // board, so that is here.
    qualityHoldChip: "Quality hold on the operation",
    downtimeChip: "Down",
    triggersMaintenance: "Breakdown",
    heldStep: "{{wo}} step {{seq}} — quality failed it",
    heldWhereToLook: "The failing inspection is on the quarantine board.",
    since: "since {{time}}",
    waitingCount: "{{count}} waiting",
    waitingUnknown: "Queue not readable",
    nextUp: "Next: {{wo}} · {{product}}",
    openQueue: "Open the station queue",
    openWorkOrder: "Open the order",
    openQuarantine: "Open the quarantine board",
    emptyTitle: "Nothing is blocked",
    // The good state names its own scope: a planner acts on this by walking
    // away, so the sentence has to say what was actually checked.
    emptyBody:
      "No downtime is open and no step is on a quality hold across the {{count}} active stations checked here.",
    emptyBodyAllFacilities:
      "No downtime is open at any plant you can see. Pick a facility to check its stations for quality holds too.",
    scopeFacility: "{{checked}} active stations checked · {{blocked}} blocked.",
    scopeAllFacilities:
      "{{blocked}} blocked. Showing open downtime for every plant — pick a facility to check quality holds as well.",
    scopeUnreadable: "{{count}} station(s) could not be read and are not counted.",
    overflow:
      "More than {{count}} intervals are open — this list is not complete. Narrow it by facility.",
    error: "Could not load the open downtime",
    forbiddenTitle: "You cannot see the downtime board",
    forbiddenBody:
      "Downtime and machine telemetry are open to plant admins, planners and maintenance. Ask a group admin if you need them.",
    offlineTitle: "No network — the board is paused",
    offlineBody: "It will fill in by itself the moment the connection is back.",
    // §5 e35 — the office END of an open stoppage (D-299 owner ruling H). The
    // floor can end a stoppage only from a station still showing live work, so
    // this board is where one nobody on the floor can end gets ended.
    end: {
      action: "End stoppage",
      title: "End this stoppage",
      subject: "{{reason}} — open since {{time}}.",
      unknownReason: "An unnamed reason",
      noteLabel: "Why it is being ended here",
      // "Running again since" is the whole point of the optional time: a
      // forgotten end found hours later must not book those hours as downtime.
      endedAtLabel: "Running again since — optional",
      endedAtHint: "Leave empty to end now. If the machine came back earlier, enter that time.",
      confirm: "End stoppage",
      ending: "Ending…",
      noteRequired: "Say why the stoppage is being ended from the office.",
      noteTooLong: "Keep the note under {{max}} characters.",
      endAtNotAfterStart: "The machine cannot be running again before it stopped at {{time}}.",
      endAtInFuture: "That time has not happened yet. Give the time the machine came back.",
      endAtUnparseable: "That time could not be read. Pick it again, or leave it empty.",
      // e35 is bounded as the floor end is (D-299 owner ruling R11): never more
      // than 24 h back. {{time}} is the server's edge (off the problem) plus a
      // retry margin, rounded up to a minute, and the sentence says AFTER: the
      // edge walks forward with the clock, so naming the edge itself invited a
      // retry the server refused again (round-3 refutation).
      endAtBeforeWindow:
        "That is more than 24 hours ago. Give a time after {{time}}, or leave it empty to end the stoppage now.",
      endAtBeforeWindowNoTime:
        "That is more than 24 hours ago. The office can record an end at most 24 hours back.",
      // The 409 is news, not a fault: somebody already ended it.
      alreadyEnded: "This stoppage was already ended at {{time}}. The board is being refreshed.",
      alreadyEndedNoTime: "This stoppage was already ended. The board is being refreshed.",
      notFound: "This stoppage is no longer on record where you can see it. Refresh the board.",
      error: "Could not end the stoppage",
      done: "Stoppage ended at {{time}}.",
      // Only when the stop was holding an order's close (e35's work_order_id).
      openHeldOrder: "Open the work order it was holding",
    },
    elapsed: {
      // Minutes stay visible past the hour on purpose: a changeover at 1 h 05
      // and one at 1 h 55 are different decisions.
      underMinute: "just now",
      minutes: "{{m}} min",
      hours: "{{h}} h",
      hoursMinutes: "{{h}} h {{m}} min",
      days: "{{d}} d",
      daysHours: "{{d}} d {{h}} h",
    },
  },

  // ---- S7 — sync conflict review (/production/conflicts) — slice 04-B-3 -----
  conflicts: {
    title: "Sync conflicts",
    // D-299 owner ruling R1: a correction applied to a COMPLETED order moves
    // the step and nothing the completion already received into stock. The
    // notice says both figures and names the one remedy, a stock adjustment,
    // which is a person's decision (module 06) and never automatic. Since
    // round 4 the server sends the notice's data ONLY when the corrected step
    // is the one the completion took its output from (`receiptSourceOperation`
    // in module 04), so the receipt sentence may say so: a correction to an
    // earlier step changes nothing the stock was derived from and shows no
    // notice at all.
    receiptUnchanged: {
      corrected:
        "Correction applied: the step now records {{good}} {{uom}} good and {{scrap}} {{uom}} scrap.",
      receipt:
        "The order was already completed and took its output from this step, so its stock receipt is unchanged: {{receipted}} {{uom}} good.",
      remedy: "If that stock is now wrong, correct it with a stock adjustment in Inventory.",
      openOrder: "Open the work order",
    },
    error: "Could not load the conflict queue",
    emptyOpenTitle: "Nothing needs review",
    emptyOpenBody:
      "Floor entries that could not be recorded as they were, or that need a second look, wait here for a decision.",
    emptyTitle: "No conflicts",
    emptyBody: "No conflicts match these filters.",
    mobileHint: "Resolving is disabled on a phone — use a desktop.",
    filter: {
      status: "Status",
      kind: "Kind",
      all: "All",
      resolved: "Resolved",
    },
    status: { open: "Open" },
    rowStatus: {
      open: "Open",
      applied_with_edit: "Applied with edit",
      discarded: "Discarded",
      acknowledged: "Acknowledged",
    },
    kind: {
      rejection: "Floor report refused",
      review_flag: "Review flag",
    },
    col: {
      when: "When",
      reason: "Reason",
      kind: "Kind",
      event: "Event",
      status: "Status",
    },
    payload: "Event payload",
    applyWithEdit: "Apply with edit",
    discard: "Discard",
    acknowledge: "Acknowledge",
    correctedPayload: "Corrected payload (JSON)",
    badJson: "That is not valid JSON.",
    note: "Note (optional)",
    noteRequired: "Note (required to discard)",
    discardWarning:
      "This event was never applied — discarding drops the floor's only record of it. Say why, for the audit trail.",
    confirmResolve: "Resolve",
    resolveFailed: "The resolution was refused.",
    // FLOOR_CONFLICT_RESOLUTION_REJECTED (e25): the corrected event no longer
    // applies — the pipeline refused it, or it asks for something that already
    // happened, such as ending a stoppage that has ended (D-299 owner ruling
    // R6). The row stays open, and the sentence names the one exit its kind
    // offers: a rejection is discarded, a review flag is acknowledged. The
    // server's English title is never relayed.
    // DOWNTIME_END_AT_INVALID on e25 (D-299 owner ruling Q2): an S7
    // apply-with-edit of a rejected End whose tablet instant is more than 24
    // hours back is refused with e35's own bound, because office acts on
    // downtime share one bound. The corrected payload cannot restate the
    // instant (it rides the envelope), so the way on is the office ender.
    endBeforeWindow:
      "The tablet ended this stoppage over 24 hours ago, too far back to record. Discard with a note, then end it on the blocked-stations board.",
    endAtInvalid:
      "The tablet's end time for this stoppage can't be applied. Discard with a note, then end it on the blocked-stations board.",
    // DOWNTIME_START_AT_INVALID on e25 (D-299 owner ruling S1): the re-apply
    // of a tap that starts a stoppage more than 24 hours back. Nothing in the
    // office opens a stoppage, so the discard is the whole way on.
    startBeforeWindow:
      "The tablet started this stoppage over 24 hours ago, too far back to record. Nothing changed. Discard with a note.",
    // The same refusal on an `op_paused` (D-299 owner rulings U4 and U1). U4
    // asked for an edit that drops the downtime reason as a second way on;
    // under U1 every re-applied pause also stops labor at the same instant and
    // takes the same window, so that edit is refused too (LABOR_AT_INVALID).
    // The sentence names the edit as no way on, in agreement with the server.
    pauseStartBeforeWindow:
      "The tablet paused this step over 24 hours ago, too far back to record. Removing the downtime reason won't help. Discard with a note.",
    // LABOR_AT_INVALID on e25 (D-299 owner ruling U1): a re-applied Start,
    // pause, resume or Complete that opens or ends no stoppage, more than 24
    // hours back. Nothing in the office restates a tap's instant, so the
    // discard is the way on.
    laborBeforeWindow:
      "The tablet's labor on this step is over 24 hours old, too far back to record. Nothing changed. Discard with a note.",
    // A refused re-applied Complete (D-299 owner ruling V4). The step is NOT
    // complete, and a discard does not complete it, so the sentence names the
    // one office act that does: S2's record-result form. {{record}}, {{tab}}
    // and {{complete}} are that form's own labels, passed in by the caller.
    // The floor's §3.2 guard ran before this refusal, so the step's quantity
    // is already recorded, and e34 completes such a step with no new quantity
    // (owner ruling W1). Both variants say so.
    // The stoppage variant: the step was paused with a reason, so completing
    // it also ends that stoppage, and e34 bounds that end like e35 does.
    completeStoppageBeforeWindow:
      'This Complete is over 24 hours old; the step stays paused. Discard with a note, then use "{{record}}" on the {{tab}} tab, ticking "{{complete}}".',
    // The labor variant: no open stoppage, so only the 24-hour labor bound
    // refused it. The step stays paused or in progress.
    completeLaborBeforeWindow:
      'This Complete is over 24 hours old; the step is not complete. Discard with a note, then use "{{record}}" on the {{tab}} tab, ticking "{{complete}}".',
    // Said after either Complete sentence (owner ruling W1): e34 completes the
    // step with no new quantity, so the reader is not asked to invent one.
    noNewQuantity: "No new quantity is needed: the step's quantity is already recorded.",
    // Said after the stoppage variant: e34 ends that stoppage too, under the
    // same 24-hour bound, so these are the form's two ways on.
    completeStoppageWayOn:
      "Enter an end within the last 24 hours, or end the stoppage on the blocked-stations board first.",
    resolutionRejected: {
      rejection:
        "This event no longer applies, so nothing was changed. Discard this conflict, with a note saying why.",
      review_flag:
        "This correction no longer applies, so nothing was changed. Acknowledge the flag to keep the numbers already applied.",
    },
    // e25's 409 FLOOR_CONFLICT_ALREADY_RESOLVED (D-299 round 9): a second
    // resolver. Shown at screen level, because the refetch the hook starts
    // takes the resolved row out of the open list.
    alreadyResolved:
      "Someone else resolved this conflict first, so your entry was not applied. The queue is refreshed.",
    // `way_on: inventory_stock_adjustment` (D-308 round 2): a floor return
    // that synced after its order closed. `returnAfterClose` is the open
    // row's own sentence; `rejectedAfterClose` answers a refused re-apply.
    wayOn: {
      returnAfterClose:
        "The order closed before this return arrived, so nothing was taken back. Discard with a note, then use a stock adjustment in Inventory.",
      rejectedAfterClose:
        "The order is closed, so this return can't be applied. Discard with a note, then use a stock adjustment in Inventory.",
    },
    // `awaiting_conflict_id` (D-308 round 2): an End refused because the tap
    // that started its stoppage is itself an open rejection in this queue.
    // The payload key is frozen at refusal time and the start leaves the open
    // list once it is resolved, so neither sentence claims the start is still
    // open (the D-308 review).
    awaiting: {
      note: "This end waits for its start, which was also refused. Resolve the start first; if you discard it, discard this end too.",
      rejected:
        "This end still waits for its start in this queue. Nothing changed. Resolve the start, then apply this end again.",
      showStart: "Show the start",
      startElsewhere:
        "The start is not on this page. If it was applied, apply this end; if discarded, discard this end too.",
    },
    // Module 06's 422 UNIT_NOT_AVAILABLE on a re-applied floor RETURN (the
    // D-308 review): the trusted re-apply lets it through, and its title is
    // English and names an API field. The conflict stays open.
    returnUnitNotAvailable:
      "The source container can't take this back now: held, in transit or emptied. Apply again once released, or discard with a note.",
    // e25's 409 FLOOR_CONFLICT_RESOLUTION_REJECTED with `mismatched_fields`
    // (D-299 round 9): an event already stored under the correction's key is
    // not this correction. {{fields}} is the list of `mismatchField` words.
    correctionMismatch: {
      rejection:
        "A different event is already recorded under this correction (differs in {{fields}}). Nothing changed. Discard with a note saying why.",
      review_flag:
        "A different event is already recorded under this correction (differs in {{fields}}). Nothing changed. Acknowledge the flag to keep the applied numbers.",
    },
    mismatchField: {
      event_type: "the kind of event",
      worker_id: "the worker",
      work_center_id: "the station",
      wo_operation_id: "the step",
    },
    nextPage: "Next page",
    prevPage: "Back",
    // §2.17's reason vocabulary — translated because an operator reads these.
    reason: {
      invalid_transition: "The step had already moved on when this arrived",
      qty_exceeds_remaining: "Reported quantity exceeds what remains",
      op_already_completed: "The step was already completed",
      wo_cancelled: "The work order was cancelled",
      stock_insufficient: "Not enough stock to issue",
      unknown_reference: "Reference could not be resolved",
      stale_snapshot: "Device clock more than 24 h off",
      qc_hold: "The step is on quality hold",
      worker_not_authenticated: "Worker did not match the signed-in session",
      // Every §5.1 kind has an applier as of 04-C-5 (`defect_handoff_flagged`
      // was the last, and `FLOOR_EVENT_KINDS_DEFERRED` is now empty), so the
      // old copy — "this event kind arrives with slice 04-C" — became a
      // statement the product could no longer make true. The reason value
      // survives for the next module that parks a kind, and the wording is now
      // about the tap rather than about a slice number a factory never saw.
      unsupported_event_kind: "This device sent an action this system does not handle",
    },
  },

  // ---- S8 — floor devices (/production/devices) — slice 04-B-3 --------------
  devices: {
    title: "Floor devices",
    register: "Register a device",
    registerFailed: "Registration was refused.",
    error: "Could not load the device registry",
    emptyTitle: "No devices registered",
    emptyBody:
      "Register the tablets that post floor events — a device the registry does not know cannot sync.",
    mobileHint: "Registry changes are disabled on a phone — use a desktop.",
    showRetired: "Show retired devices",
    col: {
      code: "Code",
      name: "Name",
      lastSeen: "Last sync",
      pending: "Queued on device",
      status: "Status",
    },
    codeNote: "Printed on the physical device; immutable once registered.",
    pendingHint: "{{count}} queued (device-reported)",
    neverSeen: "Never synced",
    active: "Active",
    retired: "Retired",
    rename: "Rename",
    retire: "Retire",
    retireConfirm: "Retire — its recorded events remain",
  },

  // ---- S6 — calibration drift (/production/calibration) — slice 04-C-2 ------
  // Also feeds the S2 Calibration tab's table (one vocabulary, two surfaces).
  calibration: {
    title: "Calibration drift",
    pickWorkCenterTitle: "Pick a work centre",
    pickWorkCenterBody:
      "Drift is judged per work centre — the machine grain until the equipment registry ships.",
    facility: "Facility",
    error: "Could not load the drift series",
    chartTitle: "Daily average variance %",
    chartAria: "Drift chart: daily average variance percentage with min/max band",
    bucketDay: "Daily",
    bucketWeek: "Weekly",
    thresholdGuide: "±{{pct}}% guide",
    bandLegend: "min/max band",
    meanLabel: "30-day mean",
    stddevLabel: "30-day spread (σ)",
    // The mean and spread of NO barrels do not exist. Rendering the wire's 0
    // for them claimed a perfectly calibrated machine nobody has measured.
    noBarrelsValue: "Not measured",
    barrelsLabel: "Barrels in window",
    // §3.8's Σ |variance cost| over the window — the ledger's own sum, the same
    // number `production.calibration_drift_detected` carries to Module 12.
    impactLabel: "Money impact (30 days)",
    thresholdsLabel: "Thresholds",
    thresholdsValue: "±{{mean}}% mean · {{stddev}}% spread",
    // Was "…pending an owner decision (D-083)". Two things were wrong with it:
    // it printed an internal decision id at a calibration technician, and it
    // described an open question as though the screen were waiting on someone.
    // The question is now settled — these are platform standards, deliberately
    // not a per-site setting — so the note says what the numbers ARE and why
    // they are the same everywhere, which is the part a technician can act on.
    thresholdsStandard:
      "Platform standards, the same at every site — a site cannot loosen the limit its own meter is judged by.",
    statusOk: "Within thresholds",
    statusBelowFloor: "Not judged — fewer than {{count}} barrels in the window",
    classification: {
      calibration_bias:
        "Calibration bias — a sustained offset; recalibrate the machine (its error is at least predictable).",
      meter_unreliable:
        "Meter unreliable — the counter's error changes barrel to barrel; measured capture is forced on this work centre's routing steps.",
    },
    emptyTitle: "No calibration records",
    emptyBody:
      "Records appear when workers complete measured-capture steps on this work centre — machine-claimed vs measured, barrel by barrel.",
    recentTitle: "Recent barrels",
    csvExport: "Export CSV",
    col: {
      measuredAt: "Measured",
      batch: "Barrel",
      claimed: "Machine said",
      measured: "Measured",
      variance: "Variance",
      variancePct: "Variance %",
      method: "Method",
      worker: "Worker",
    },
    method: {
      scale_weight: "Scale weight",
      meter_counter: "Meter counter",
      manual_tape: "Manual tape",
    },
    // The S2 tab (§4 tab rule — present only now that the table exists).
    tabEmptyTitle: "No calibration records on this order",
    tabEmptyBody:
      "Measured-capture steps record machine-claimed vs measured on completion — nothing has been recorded yet.",
    tabError: "Could not load the calibration records",
  },

  // ---- S5 — the shift scoreboard (§4 S5, slice 04-C-4) ---------------------
  shifts: {
    title: "Shift scoreboard",
    facility: "Facility",
    shiftDate: "Shift date",
    // §9.7, said where the filter is rather than in a tooltip nobody opens.
    // The pack's shift codes as a person says them (D-455); an unknown
    // code falls back to itself.
    shiftNames: {
      DAY: "Day",
      NIGHT: "Night",
      MORNING: "Morning",
      AFTERNOON: "Afternoon",
      EVENING: "Evening",
      OFFICE: "Office hours",
    },
    shiftDateNote:
      "The day the shift starts. A night shift that runs past midnight is listed under the day it began.",
    tableCaption: "Quantity, first-pass yield and downtime per shift and work centre",
    // The row's own shift. Two shifts on one date means the same work centre
    // twice, and without this the two rows are indistinguishable.
    colShift: "Shift",
    colGood: "Good",
    colScrap: "Scrap",
    colFpy: "First-pass yield",
    colDowntime: "Downtime",
    // Present and zero until 04-C-5's writer lands — never absent (see
    // `defectsPending`, which explains the zero rather than leaving it bare).
    colHandedOff: "Defects handed off",
    colFlagged: "Defects flagged",
    colWorkers: "Workers",
    colState: "State",
    totalGood: "Good today",
    totalFpy: "First-pass yield",
    totalDowntime: "Downtime",
    minutes: "{{n}} min",
    // A shift that produced nothing has NO yield — which is not 0%.
    noYield: "—",
    worstBadge: "Lowest yield",
    stateLive: "Live",
    stateFinalized: "Closed",
    emptyTitle: "Nothing recorded for this shift date",
    emptyBody:
      "The board fills as the floor reports quantity. Check the date, or pick another facility.",
    error: "Could not load the scoreboard",
    // 04-C-5: the columns now MOVE. The note that used to explain a permanent
    // zero is replaced by one that explains what the numbers mean, because a
    // reader seeing a defect land on a shift that closed yesterday will
    // otherwise assume the board is wrong.
    defectsPending:
      "A defect counts against the shift that produced it, even a closed one, and the shift that flagged it.",
    defectsScrapMoves:
      "Scrapping one moves the quantity onto the producing shift's scrap and yield.",
    handoffTitle: "Defect hand-offs",
    handoffPending:
      "No hand-offs on this shift date. A hand-off appears when a worker flags defective work from an earlier shift.",
    handoffError: "Could not load the hand-off ledger",
    handoffCaption: "Defective work one shift flagged against another, and what was decided",
    handoffColProduced: "Produced by",
    handoffColFlagged: "Flagged by",
    handoffColQty: "Quantity",
    handoffColReason: "Reason",
    handoffColState: "Decision",
    handoffShiftCell: "{{code}} · {{date}}",
    handoffWorkerUnknown: "Crew — no single worker",
    handoffDisposition: {
      pending: "Undecided",
      rework: "Rework",
      scrap: "Scrap",
      accepted_as_is: "Accepted as is",
    },
    handoffRework: "Rework",
    handoffScrap: "Scrap",
    handoffAccept: "Accept as is",
    // D-299: under a disabled Rework on an order that is no longer released or
    // in progress. It names the decisions that still apply and where the re-do
    // goes, because a disabled control with no way out is a dead end. Scrap is
    // qualified, not promised: e19's C6 fence still refuses it when quality
    // already posted the step's scrap (owner ruling F2), and its 409 then names
    // the doors that open. "Quarantine" is module 07's word for its lot record
    // (D-123 ruling 9), and it names module 07's BOARD, because module 04 may say
    // "quarantine" only about module 07's surface, never about a flag of its own
    // (hold-vocabulary.test.ts). The lots sentence is for a COMPLETED order only — its
    // output was received into stock; `...NoStock` is the cancelled/draft
    // spelling, where nothing was (owner ruling F3).
    handoffReworkStock:
      "To redo goods already in stock, plan new work against their lots or quarantine them on the quarantine board.",
    handoffReworkBlockedNoStock:
      "The order is {{status}}, so the step can't reopen for rework. Accept as is, or Scrap unless quality already scrapped it.",
    // D-299 owner rulings F1/F2: the sentence under a REFUSED decision, built
    // from the refusal's own `available_dispositions`, so it names only the
    // decisions the server would accept. {{decisions}} is a localized "or" list.
    handoffRefusedNotLive:
      "The work order's status is {{status}}, so this step can no longer be reopened for rework. What still applies: {{decisions}}.",
    handoffRefusedScrapAttributed:
      "Quality has already scrapped this step, so scrapping it here would count the loss twice. What still applies: {{decisions}}.",
    // D-299 owner rulings: a CLOSED order offers no decision at all (e19
    // refuses every disposition there, §9.4), so the row says why instead of
    // showing three controls that can only fail.
    handoffOrderClosed:
      "The work order's status is {{status}}, so no decision can be recorded on this hand-off any more.",
    handoffNoteLabel: "Why",
    // Scrap is the one that moves a number onto another shift's board, so the
    // form says why it insists.
    handoffNoteHint: "Required for scrap — it moves the quantity onto the producing shift's yield.",
    handoffDeciding: "Saving…",
    handoffDecideError: "Could not record that decision",
    handoffPhotos: "{{n}} photo(s)",
  },

  // ---- S14 — piece-rate entries (/production/piece-rate) -------------------
  //
  // The words here carry three absences, and each one is a sentence rather than
  // a blank: money hidden by PERMISSION (§7 — key absence), money empty by
  // MATURITY (§12 Q1 — no rate table), and no rows at all because no routing
  // step is flagged piece-rate eligible. D-118's rule is that a dead thing says
  // WHICH reason it is dead for, in words a supervisor can act on.
  pieceRate: {
    title: "Piece-rate entries",
    tableCaption: "Piece quantity captured per worker, shift and step",
    colShiftDate: "Shift date",
    colWorker: "Worker",
    colFacility: "Facility",
    colOperation: "Step",
    colQty: "Piece quantity",
    colRate: "Rate",
    colAmount: "Amount",
    colStatus: "State",
    colReported: "Reported",
    dateFrom: "Shift date from",
    dateTo: "Shift date to",
    statusFilter: "State",
    statusAny: "Any state",
    // §2.13's vocabulary. "Captured" is the only one this release can produce.
    statusCaptured: "Captured",
    statusPriced: "Priced",
    statusSettled: "Settled",
    shiftDateNote:
      "The shift's start date: a night shift past midnight counts, and is paid, under the day it began.",
    loadMore: "Load more",
    error: "Could not load the piece-rate entries",
    // The FIXABLE half of D-118: the columns are here and empty, and the reason
    // is a decision that has not been taken yet, not a fault.
    unpricedNote: "No rate is applied to these quantities.",
    // The PERMISSION half: the columns are ABSENT, and an absence nobody
    // explains reads as a column somebody forgot.
    moneyHiddenNote:
      "Rates and amounts are not shown to your role. This ledger shows the quantity captured; ask a group admin if you need the pay figures.",
    moneyPartialNote:
      "Rates and amounts are hidden because some entries are other workers'. Filter to your own to see pay.",
    emptyTitle: "No piece quantity has been captured",
    // The reason, in words. This is the one a factory can act on: the flag is
    // authored on the routing step (spec 01 §2.11) and is off by default.
    emptyBody: "Entries appear when the floor reports good quantity on a piece-rate step.",
    emptyFilteredTitle: "No entries match these filters",
    emptyFilteredBody: "Widen the shift dates, pick another facility, or clear the state filter.",
    forbiddenTitle: "You cannot see the piece-rate ledger",
    forbiddenBody:
      "Piece-rate quantities are open to finance, plant admins and group admins; a worker sees their own. Ask a group admin if you need them.",
    offlineTitle: "No network — the ledger is paused",
    offlineBody: "It will fill in by itself the moment the connection is back.",
  },

  // ---- S9/S10 — the floor screens (station home + op run) — slice 04-B-3 ----
  floor: {
    // Shown beside a status chip whose EFFECTIVE status (snapshot + this
    // device's own queued taps, §3.4) differs from the server snapshot.
    pendingSync: "pending sync",
    // ---- The module-04 tiles on the /floor hub -----------------------------
    // WHY THESE EXIST (operator-shift audit, 2026-08-17): S9's station picker
    // and S11's my-shift shipped with routes, screens and unit tests — and NO
    // control anywhere in the product led to either. Every test reached them
    // with page.goto, so an operator without a QR in hand could not claim a
    // station and an operator ending a shift could not open their own tally.
    // The hub tiles these keys label are the fix. The my-shift TILE reuses
    // `myShift.title` (the QualityTile precedent: the tile says exactly what
    // the screen it opens says); the station tile cannot reuse `picker.title`
    // ("Choose a station") because for the mounted-tablet case the picker
    // immediately offers "Back to {{name}}" — the tile names the DESTINATION
    // ("My station"), not the picker mechanics.
    home: {
      station: "My station",
      stationSub: "Open the work queue — pick your station from the list, no QR needed.",
      myShiftSub: "Your tally for today — good, scrap, steps finished.",
    },
    // ---- S11 my-shift + S12 wall mode (§4 floor rows 11/12, slice 04-C-4) --
    back: "Back",
    myShift: {
      title: "My shift",
      shiftLine: "{{code}} · {{date}}",
      noShift: "No shift is running right now.",
      good: "Good",
      scrap: "Scrap",
      ops: "Steps finished",
      pieceQty: "Piece quantity",
      // §12 Q1 is open: quantity is capturable, a rate is not — and a number a
      // worker would expect on a payslip must not be invented here.
      rateNote: "Quantities only, with no pay amount.",
      errorTitle: "Could not load your shift",
      errorBody: "Nothing you recorded is lost. Tap retry, or check the connection banner.",
      offlineWhat: "your shift tally",
    },
    wall: {
      title: "Shift board",
      fpyLabel: "First pass",
      noYield: "—",
      worst: "Lowest",
      // The header's scope line: which shift, on which business date. Derived
      // from the rows ON THE GLASS, so it can never name a shift the board is
      // not showing.
      scope: "Shift {{shift}} · {{date}}",
      // The liveness word. It rides WITH the age (never instead of it): a green
      // LIVE chip beside an hour-old number is the confident-wrong-number
      // failure §9.16 exists to prevent.
      live: "LIVE",
      stale: "STALE",
      // What the WIRE knows about a station, which is a SHIFT state, not a
      // machine state. e17 carries `finalized`; it does not carry
      // running/setup/down, and a wall board may not guess one.
      stateOpen: "Open",
      stateClosed: "Closed",
      outputLabel: "Good output",
      // The worded diagnosis strip. §9.11's downtime MINUTES are on the wire;
      // the cause is not, so this says the minutes and stops there.
      downtime: "Downtime {{n}} min",
      footScrap: "Scrap",
      footRework: "Rework",
      footWorkers: "Workers",
      // "PLANT", not "shift". These five are the SERVER's `facility_totals`,
      // which are scoped to the polled business date (named in the header), not
      // to the shift instance the tiles are drawn from — a date can carry a
      // closed shift as well as the running one. Calling them shift totals would
      // put a number on a wall that disagrees with the tiles above it.
      totalOutput: "Plant output",
      totalYield: "First pass",
      totalScrap: "Scrap",
      totalRework: "Rework",
      totalDowntime: "Downtime",
      minutesUnit: "min",
      // The one case where the server's per-DATE totals cannot honestly stand
      // under per-SHIFT tiles: a changeover tick with two shifts still open.
      totalsMixed: "Plant totals are hidden while the board shows two dates ({{dates}}).",
      // Three units, not raw seconds. The stamp is read at three metres by
      // somebody who is not stopping: "Updated 10805s ago" is honest and
      // useless, "Updated 3h ago" is the same fact in a form that lands.
      updatedAgoSeconds: "Updated {{n}}s ago",
      updatedAgoMinutes: "Updated {{n}} min ago",
      updatedAgoHours: "Updated {{n}}h ago",
      neverLoaded: "Waiting for the first update…",
      empty: "Nothing reported yet on this shift.",
      more: "+{{n}} more stations",
    },
    picker: {
      title: "Choose a station",
      scanLabel: "Scan the station QR",
      scanPlaceholder: "Scan or type the station code…",
      scanMiss: "That code is not a station or operation QR.",
      resume: "Back to {{name}}",
      errorTitle: "Could not load the stations",
      errorBody: "The station list needs a connection the first time.",
      emptyTitle: "No stations",
      emptyBody: "This facility has no work centres yet — the office sets them up.",
      open: "Open this station",
    },
    station: {
      title: "Station",
      changeStation: "Change station",
      what: "this station's queue",
      needsReview: "{{count}} item(s) need review — open the tray",
      shiftLeft: "{{minutes}} min left",
      myOpen: "My open steps",
      queue: "Queue",
      emptyTitle: "Nothing queued at this station.",
      emptyBody: "Released work for this station will appear here.",
      errorTitle: "Could not load the station",
      errorBody: "The queue could not be loaded — taps still queue on this device.",
      progress: "{{reported}} of {{planned}}",
      openOp: "Open",
      // 04-C-5 — the two §5 r15 card states, worded on the CARD so the
      // operator learns them before walking to the machine.
      reworkBadge: "Re-do",
      holdBadge: "On quality hold",
      noDevices:
        "This facility has no registered floor device, so taps cannot sync — ask the office to register this tablet (Production → Devices).",
      device: "This device: {{code}} · {{name}}",
      changeDevice: "Change",
      pickDevice: "Which device is this?",
      pickDeviceBody:
        "Pick the code printed on this tablet — everything you record is marked with it.",
      thisDevice: "This device",
    },
    op: {
      title: "Operation",
      what: "this operation",
      notHereTitle: "Not at this station",
      notHereBody:
        "This operation is not in the pinned station's queue. Scan the station QR printed at its work centre.",
      staleIdentity:
        "This tablet's station details are out of date. Connect to the network once to refresh them — until then your taps cannot be recorded.",
      // D-118: the sentence a DEAD BUTTON carries, at the button. Short enough
      // to sit on a second line inside a glove target, and it names the move,
      // not the condition — an operator cannot act on "worker_id is undefined".
      blockedIdentity: "Find signal once first — this tablet cannot say who is working",
      blockedDevice: "Choose which tablet this is, at the top of this screen",
      blockedSending: "Sending…",
      pendingHere: "{{count}} tap(s) for this step are still on this device.",
      applied: "Recorded.",
      queued: "Queued on this device — it sends when there is signal.",
      conflicted: "Needs review — {{reason}}. The office resolves it from the conflict queue.",
      openTray: "Open tray",
      needsAttention: "This tap could not be sent — it is waiting in the tray.",
      doneNote: "This step is finished here.",
      start: "Start",
      pause: "Pause",
      resume: "Resume",
      complete: "Complete",
      guardNoQty: "Report a quantity before you tap Complete.",
      guardCalibration: "Record the calibration below before you tap Complete.",
      guardSerial: "Scan or key the serial below before you tap Complete.",
      // 04-C-5 §2.2/§3.2. Unlike the three guards above, this one blocks the
      // work itself rather than the completion — so it says "do not start",
      // and it names who can clear it.
      guardQcHold:
        "This step is on quality hold. Ask the office or a quality inspector to release it.",
      reworkNote:
        "This step was re-opened for rework — what you report here is booked as re-done work, not as new output.",
      reportQty: "Report quantity",
      qtyGood: "Good",
      qtyScrap: "Scrap",
      scrapReason: "Scrap reason",
      noScrapReasons: "No scrap causes in the catalog — the office manages them in Waste settings.",
      nothingReported: "Report a good or scrap quantity first.",
      reasonRequired: "Pick a scrap reason.",
      reportButton: "Report",
      qtyIdentity: "Quantity",
      // The §4 row 10 numpad (D-091) — the glove keys a decimal is entered
      // with. The decimal key itself is labelled with the LOCALE's separator
      // (a glyph, not a dictionary key); these are the pad's words.
      numpadOpen: "Numpad",
      numpadDone: "Done",
      numpadBackspace: "Delete the last digit",
      numpadDecimal: "Decimal separator",
      materialIdentity: "Material",
      material: "Material",
      materialLine: "{{issued}} of {{planned}} {{uom}} issued",
      issueNoScan: "Issue without scan",
      noMaterials: "No expected materials for this step.",
      scanLabel: "Scan a lot or unit QR",
      scanPlaceholder: "Scan the material label…",
      scanTarget: "Scanned: {{code}}",
      manualTarget: "Issuing: {{name}}",
      // D-081 (04-B-4): the RETURN — the inverse of the scan issue, same
      // screen, same scan; the mode owns the sign.
      materialMode: "Issue or return",
      modeIssue: "Issue",
      modeReturn: "Return",
      returnNoScan: "Return without scan",
      scanReturnTarget: "Returning scanned: {{code}}",
      manualReturnTarget: "Returning: {{name}}",
      returnButton: "Return to stock",
      returnIdentity: "Material return",
      materialQty: "Quantity",
      uom: "Unit",
      noUom: "No unit of measure is known for this scan — issue from an expected line instead.",
      materialNeedsQty: "Enter how much first",
      issueButton: "Issue",
      // 04-C-1: the downtime button + reason grid + open-downtime indicator.
      // The old `pending04c` note retired its downtime clause with them; the
      // rest of the note (below) keeps naming what is still absent (D-033).
      downtime: "Downtime",
      downtimeButton: "Log downtime",
      downtimeIdentity: "Downtime",
      downtimeEndIdentity: "Downtime end",
      downtimeOpenNote: "{{count}} downtime interval(s) open at this station.",
      downtimeSince: "Down since {{time}}",
      downtimeEnd: "End downtime",
      downtimeNoEnd: "Opened from the office — it ends there.",
      downtimeNoReasons:
        "No active downtime reasons in the catalog — the office manages them under Downtime reasons.",
      // §3.3 step 10 — the flag (04-C-5). The `pending04cRest` note that stood
      // here RETIRED: every §5.1 control the floor screen owes is on the glass.
      flagDefect: "Flag defect",
      defectQty: "Defective quantity",
      defectProducedAt: "Made at which step?",
      // §2.11's "defaults to same op", said as the operator would say it.
      defectProducedHere: "This step",
      // The same default, said when there is NOTHING to pick — the queue only
      // carries this station's steps, so a defect made at another station has
      // no option to name. The operator is told what the flag will assert
      // rather than left to assume it asserts nothing.
      defectProducedHereOnly:
        "This is recorded against this step. Name another machine's step in the note.",
      defectNotes: "Note (optional)",
      defectSend: "Flag it",
      defectIdentity: "defect flag",
      defectNothingFlagged: "Enter how much is defective.",
      // The evidence photo (D-096) — the camera control that replaced the
      // stated-pending note of 04-C-5. The blob queues durably on the tablet
      // and uploads through the three-call flow when the network returns.
      defectPhoto: "Evidence photo",
      defectPhotoAdd: "Add photo",
      defectPhotoAddAnother: "Add another photo",
      defectPhotoRetake: "Retake",
      defectPhotoPreview: "Evidence photo {{n}}",
      defectPhotoHint:
        "Optional, but a photo is what settles a shift dispute. It is saved on this tablet with the flag and uploads when the network returns.",
      defectPhotoTooLarge: "That photo is over the 20 MB limit — retake it.",
      defectPhotoWrongType:
        "That file is not a photo this system accepts (JPEG, PNG, WebP or HEIC).",
      defectPhotoLimit: "No more than {{max}} photos on one flag.",
      cancel: "Cancel",
      // 04-C-2: the calibration form (§3.3 step 7).
      calibration: "Calibration",
      calibrationIdentity: "Calibration",
      calibrationBatch: "Barrel / batch label",
      calibrationBatchPlaceholder: "e.g. B-0217",
      calibrationClaimed: "Machine says",
      calibrationMeasured: "Measured",
      calibrationUomPlaceholder: "e.g. m",
      calibrationMethod: "How was it measured?",
      calibrationMethods: {
        scale_weight: "Scale weight",
        meter_counter: "Meter counter",
        manual_tape: "Manual tape",
      },
      calibrationScan: "Barrel QR (optional)",
      calibrationScanPlaceholder: "Scan the output barrel label…",
      calibrationProblem:
        "The barrel label, the unit, and both positive quantities are required — the machine's claim and your measurement.",
      calibrationRecord: "Record calibration",
      // 04-C-3: the serial/lot capture form (§3.3 step 8).
      serial: "Serial / lot",
      serialIdentity: "Serial capture",
      serialOpen: "Capture a serial or lot",
      serialDirection: "Which way?",
      serialDirections: {
        produced: "Came off this machine",
        consumed: "Went into this machine",
      },
      serialDirectionNote: {
        produced: "Recording what this step PRODUCED — this is what a completion needs.",
        consumed: "Recording what this step CONSUMED — the input side of the lineage.",
      },
      serialScanLabel: "Scan the label",
      serialScanPlaceholder: "Pull the trigger, or type the code…",
      serialScanned: "Scanned: {{code}}",
      serialNo: "Engraved serial (if the cable is marked)",
      serialNoPlaceholder: "e.g. KZ-2026-0042",
      serialQty: "Measured length / weight",
      serialCapture: "Save capture",
      serialProblem: {
        nothing_identified: "Scan the label or type the engraved serial — one of the two.",
        qty_required: "Enter the measured quantity.",
        uom_required: "Enter the unit.",
        // Two labels in one capture: a scanned code sitting in the engraved
        // serial box beside a different scan. Names the box to clear rather
        // than picking a drum for the operator.
        scan_in_serial:
          "The engraved serial box holds a scanned label that differs from the scan box. Clear one; a capture names one drum.",
      },
      // The one refusal an operator can fix AT THE MACHINE. `unknown_reference`
      // is a server enum; this names the code and the next move instead.
      scanUnresolved:
        "“{{code}}” matched nothing in this factory's stock. Check the label, or type the engraved serial instead. Your tap is saved for the office —",
    },
    qr: {
      title: "Operation QR",
      noStationTitle: "No station pinned",
      noStationBody: "Scan the station QR first — it anchors what this device may do.",
      elsewhereTitle: "Not at this station",
      elsewhereBody:
        "This operation is not in the pinned station's queue. Scan the QR at its work centre.",
      noSnapshotTitle: "Cannot resolve offline",
      noSnapshotBody:
        "Connect once at your station to load its queue, then scan the operation QR again.",
    },
  },

  // ---- RFC 9457 problem codes (spec 04 §5/§9) -------------------------------
  // One human sentence per code slice 04-A can raise; screens fall back to the
  // problem `title` for anything unlisted.
  problem: {
    WORK_ORDER_NOT_FOUND: "Work order not found.",
    WORK_ORDER_OPERATION_NOT_FOUND: "That operation was not found on this work order.",
    MATERIAL_ISSUE_NOT_FOUND: "The material issue being reversed was not found.",
    PRODUCT_NOT_FOUND: "Product not found.",
    FACILITY_NOT_FOUND: "Facility not found.",
    WORK_CENTER_NOT_FOUND: "Work centre not found.",
    BOM_NOT_FOUND: "That BOM version was not found.",
    ROUTING_NOT_FOUND: "That routing version was not found.",
    NO_EFFECTIVE_BOM: "No BOM version is effective for this product at this facility today.",
    NO_EFFECTIVE_ROUTING:
      "No routing version is effective for this product at this facility today.",
    WO_INVALID_TRANSITION: "The order cannot make that state change from where it is.",
    WO_NOT_EDITABLE:
      "Only a draft can be edited. Priority is the exception — the dispatch board can change it while the work runs.",
    WO_CLOSED_IMMUTABLE: "The order is closed — nothing about it can change any more.",
    WO_RELEASE_SHORTAGES_UNACKNOWLEDGED:
      "Components are short — confirm to release with the shortage on record.",
    WO_COMPLETE_BLOCKED:
      "An operation is neither completed nor skipped, so the order cannot complete.",
    WO_CLOSE_BLOCKED: "Something is still open against this order — it cannot close yet.",
    WO_CANCEL_DISPOSITION_REQUIRED:
      "Say what happens to the produced quantity and issued material before cancelling.",
    WO_OUTPUT_APPORTIONMENT_INVALID:
      "The BOM's output shares do not sum to 100% — the cost pool cannot be divided.",
    OPERATION_NOT_ACTIONABLE: "This step cannot take that action in its current state.",
    // e34's stated ended_at later than now (D-299 owner ruling V3). Only the
    // record-result form states a step's end, so the sentence names that field.
    OPERATION_END_AT_INVALID:
      "The end time is in the future, so nothing was saved. Enter when the step actually ended.",
    // e34's stated started_at later than now (D-299 owner ruling W2), the
    // start-side twin of the sentence above, on the same form.
    OPERATION_START_AT_INVALID:
      "The start time is in the future, so nothing was saved. Enter when the step actually started.",
    MEASURED_CAPTURE_REQUIRED:
      "This step requires measured capture — a calibration record (machine-claimed vs measured) must exist before it completes. The floor records it on the completion sheet.",
    SERIAL_CAPTURE_REQUIRED:
      "This step needs a serial or lot before it completes. Scan it on the floor, or key the traveler's engraved serials with the result.",
    SCRAP_REASON_REQUIRED: "A scrap reason is required when any scrap is reported.",
    SCRAP_REASON_UNKNOWN: "That scrap reason is not in your list of scrap reasons.",
    MATERIAL_RETURN_EXCEEDS_ISSUED:
      "The return is larger than what remains issued, net of returns already booked.",
    WO_OUTPUT_CURRENCY_MISMATCH:
      "A by-product is priced in a different currency than the order — that cannot be credited.",
    TRAVELER_GLYPH_COVERAGE: "The traveler contains characters the bundled fonts cannot print.",
    // ---- a work order PEGGED to a customer order line (D-290): the refusals
    // module 09 raises through the create and the release. Each names what
    // holds the work order back and what lifts it.
    ORDER_LINE_NOT_FOUND: "That customer order line was not found.",
    ORDER_NOT_CONFIRMED:
      "The customer order is waiting for approval. Work can be planned for it once it is confirmed.",
    ORDER_TERMINAL:
      "The customer order is already shipped, delivered, closed or cancelled, so no work can be planned for it.",
    // The peg admits an `open` or `in_production` line only, so a `ready`
    // line is refused this too — the sentence names all three states.
    ORDER_LINE_TERMINAL:
      "That customer order line is ready, shipped or cancelled, so no more work can be planned or released for it.",
    ORDER_LINE_PRODUCT_MISMATCH:
      "The customer order line sells a different product. A work order for it must make that product.",
    CREDIT_BLOCKED:
      "The customer order is on credit hold, so this work order can't be released. Release it once the hold lifts or prepayment arrives.",
    SHIPMENT_OPEN:
      "A shipment is being picked from this customer order line. Ship or cancel that shipment before releasing the work order.",
    // ---- §5 e35, the office end of an open stoppage (D-299 ruling H). The S13
    // confirm renders the first two AT their controls with the time the
    // problem carries; these are the plain fallbacks any other surface gets.
    DOWNTIME_LOG_NOT_FOUND: "That stoppage was not found.",
    DOWNTIME_ALREADY_ENDED: "That stoppage has already been ended.",
    DOWNTIME_END_AT_INVALID:
      "The end time must be after the stoppage began, not later than now, and no more than 24 hours ago.",
    // ---- module 06's container refusals, met through e30 and e25 (the D-308
    // review). Their titles are English and name API fields, so the area
    // says them itself. A return has its own sentences (materials and
    // conflicts); these are the fallbacks for anything else.
    UNIT_NOT_AVAILABLE:
      "That container cannot be used for this right now, so nothing was changed. It may be held by quality, in transit or empty.",
    UNIT_NOT_FOUND: "That container was not found.",
  },
} as const;

// The dictionary SHAPE, with values widened to `string` (src/i18n-shape.ts).
export type ProductionDict = Translated<typeof productionEn>;

// ru is TRANSLATED (see production.ru.ts) and pinned by tools/ci/i18n-parity.ts,
// which fails if `ru` ever regresses to en values. The other locales import
// their own production.<locale>.ts directly, so this module never bundles them.
export const productionRu: ProductionDict = productionRuDict;
